import React, { useEffect, useState } from "react";
import Button from "../ui/Button";
import { RiAiGenerate } from "react-icons/ri";
import { CiCircleInfo } from "react-icons/ci";
import Sidebar from "../PPT/Sidebar";
import TextExtractor from "../PPT/TextExtractor";
import { generateTableDataUsingLLM } from "../../utils/function";
import { LAYOUT_OPTIONS } from "../../data";
import {
  containerName,
  PPT2IMG_BACKEND_URL,
  sasToken,
  storageAccountName,
} from "../../data/constant";
import { BlobServiceClient } from "@azure/storage-blob";
import pptxgen from "pptxgenjs";
import { FaCheckCircle } from "react-icons/fa";

function InsertTable({ isOpen, setIsOpen, onInsertTable, activeSlide }) {
 
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [tableTitle, setTableTitle] = useState("");
  const [fileUploadError, setFileUploadError] = useState("");
  const [textAreaContent, setTextAreaContent] = useState("");
  const [extractedText, setExtractedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [_isDisabled, setIsDisabled] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [pptGenTableData, setPptGenTableData] = useState(null);
  const [isGeneratedByContent, setIsGeneratedByContent] = useState(false);

  function getTextFromObjects(data) {
    console.info("Extracting text from slide data objects");
    if (!data) return;
    for (const item of data) {
      if (item.type === "text") {
        const combinedText = item.data.map((obj) => obj?.text?.trim()).join(". ") + ".";
        return combinedText;
      }
    }
    return false;
  }

  useEffect(() => {
    const text = getTextFromObjects(activeSlide?.slideDataArray);
    setTextAreaContent(text || '');
    setImageUrl("");
  }, [isOpen]);

   useEffect(() => {
    if (!isOpen) {
      setIsContentVisible(false);
      setTableTitle("");
      setFileUploadError("");
      setTextAreaContent("");
      setIsLoading(false);
      setIsDisabled(true);
      setIsGeneratedByContent(false);
    }
  }, [isOpen]);

  async function uploadFileToBlob2(fileBlob, fileName) {
    console.info("Uploading file to Azure blob storage");
    try {
      const blobServiceClient = new BlobServiceClient(
        `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
      );
      const containerClient = blobServiceClient.getContainerClient(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(fileName);

      await blockBlobClient.uploadBrowserData(fileBlob, {
        blobHTTPHeaders: { blobContentType: fileBlob.type },
      });

      const fileUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${fileName}`;

      const response = await fetch(PPT2IMG_BACKEND_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pptxUrl: fileUrl }),
      });

      if (!response.ok) {
        throw new Error("Failed to convert PPTX to PDF");
      }

      const data = await response.json();
      return data.uploadedUrls[0];
    } catch (error) {
      console.info("Error uploading file:", error);
    }
  }

  async function generateTable(extractedData) {
    console.info("Generating PPT slide with table");
    return new Promise(async (resolve) => {
      let pptx = new pptxgen();
      let slide = pptx.addSlide();

      slide.addTable(extractedData, {
        ...LAYOUT_OPTIONS.LAYOUT_1_MEDIA_FULL_OPTIONS_TABLE,
      });

      pptx.write("base64").then(async (base64String) => {
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const pptBlob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        });

        const res = await uploadFileToBlob2(pptBlob, `codemonk.pptx`);
        setImageUrl(res);
        resolve(res);
      });
    });
  }

  async function firstStepGenerateTable(textAreaContent) {
    console.info("Generating table data and preview image");
    setIsLoading(true);
    const extractedData = await generateTableDataUsingLLM(textAreaContent);
    if (!extractedData) {
      return;
    }
    setPptGenTableData(extractedData);
    await generateTable(extractedData);
    setIsLoading(false);
  }

  return (
    <Sidebar
      open={isOpen}
      setOpen={setIsOpen}
      title="Insert Table"
      isLoading={isLoading}
      isDisabled={isLoading || !imageUrl}
      rightCTA={isLoading ? "Please wait..." : "Insert"}
      onConfirm={async () => {
        setIsLoading(true);

        if (!imageUrl) {
          try {
            firstStepGenerateTable(extractedText);
          } catch (error) {
            setFileUploadError("Failed to generate table data. Please try again.");
            setIsLoading(false);
            return;
          }
        } else {
          setIsOpen(false);
          setIsLoading(false);
          onInsertTable({ extractedData:pptGenTableData, tableTitle, imageUrl, isGeneratedByContent });
        }
      }}
    >
      {imageUrl ? (
        <>
          <div className="flex items-center gap-2 px-4 py-2 bg-green-200 text-black rounded-md">
            <FaCheckCircle className="text-lg bg-green-500 text-white rounded-full p-1" />
            <span>Table generated successfully</span>
          </div>
          <div className="relative">
            <img
              src={imageUrl}
              alt="Generated Table"
              className="w-full object-contain"
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between gap-4 mb-4">
            <Button
              type={!isContentVisible ? "solid" : "outline"}
              onClick={() => {
                setIsDisabled(true);
                setIsContentVisible(false);
                setIsGeneratedByContent(false);
              }}
              disabled={isLoading}
              className="w-1/2"
            >
              Upload File
            </Button>
            <Button
              onClick={() => {
                setIsContentVisible(true);
                setIsGeneratedByContent(true);
              }}
              className="w-1/2"
              disabled={isLoading}
              type={isContentVisible ? "solid" : "outline"}
            >
              Slide Content
            </Button>
          </div>

          {!isContentVisible ? (
            <div>
              <h2 className="text-base font-bold mb-4">Upload File</h2>
              <TextExtractor
               allowedFileTypes={[".xlsx", ".xls", ".csv"]}
                loading={isLoading}
                onDelete={() => {
                  setIsDisabled(true);
                }}
                onSuccess={(text) => {
                  setExtractedText(text);
                  firstStepGenerateTable(text)
                  setIsDisabled(false);
                }}
                disabled={isLoading}
              />

              {fileUploadError && (
                <div className="text-red-500 text-sm mb-2">
                  {fileUploadError}
                </div>
              )}
              <div className="p-2 bg-gray-100 flex gap-3 items-center rounded-md text-sm">
                <CiCircleInfo className="text-3xl mt-1 text-blue-500" /> A slide
                can have up to 2 media. Additional media will be added to a new
                slide.
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <h2 className="text-base font-bold ">Content</h2>

              <textarea
                placeholder="Enter content here"
                value={textAreaContent}
                readOnly
                onChange={(e) => setTextAreaContent(e.target.value)}
                disabled={isLoading}
                className="w-full h-[200px] p-2 border text-sm font-serif leading-10  border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button
                disabled={isLoading || !textAreaContent}
                onClick={() => {
                  setIsContentVisible(true);
                  firstStepGenerateTable(textAreaContent)
                }}
                className=""
                type={"outline"}
              >
                <RiAiGenerate />{" "}
                {isLoading ? "Generating Table" : "Generate Table"}
              </Button>

              <div className="p-2 bg-gray-100 flex gap-3 items-start rounded-md text-sm">
                <CiCircleInfo className="text-xl mt-1 text-blue-500" /> Tables
                generated from slide content will remain on the same slide;
                excess media moves to the next slide.
              </div>
            </div>
          )}
        </>
      )}
    </Sidebar>
  );
}

export default InsertTable;
